import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { DataverseActionsEnum, DataverseSubActionEnum } from '../models/dataverse-actions-enum';
import { DataverseLookup } from '../models/dataverse-lookup';
import { DataverseViewEnum } from '../models/dataverse-view-enum';
import { Dto } from '../models/dto';
import { DtoGroup } from '../models/dto-group';
import { ExportTemplateModel } from '../models/export-template.model';
import { Field } from '../models/fields';
import { DataverseApiService } from './dataverse-api.service';
import { EventBusService } from '../../../services/event-bus.service';

/**
 * Injectable service for the Dataverse
 */
@Injectable({
  providedIn: 'root'
})
export class DataverseV2Service {
  /**
   * Consider unique name in search
   */
  considerUniqueNameInSearch = false;

  /**
   * Observable indicating whether the search is active
   */
  isSearchActive$: Observable<boolean>;

  /**
   * Active view
   */
  activeView$: Observable<DataverseViewEnum>;

  /**
 * Observable representing the active action in the Dataverse
 */
  activeAction$: Observable<DataverseActionsEnum>;

  /**
   * Observable representing the active sub-action in the Dataverse
   */
  activeSubAction$: Observable<DataverseSubActionEnum>;

  /**
  * Observable representing if the export model should be updated
  */
  updateExportTemplateModel$: Observable<boolean>;

  /**
   * Observable representing the active DTO (Data Transfer Object) in the Dataverse
   */
  activeDto$: Observable<Dto>;

  /**
   * Observable representing the selected fields in the Dataverse
   */
  selectedFields$: Observable<Field[]>;

  /**
   * Observable representing the active field in the Dataverse
   */
  activeField$: Observable<Field>;

  /**
 * Observable representing the DTO List in the Dataverse
 */
  dtoList$: Observable<Dto[]>;

  /**
   * Array containing system fields in the Dataverse
   */
  systemFields: any[];

  /**
   * Lookup array for development status in the Dataverse
   */
  devStatusLookup: DataverseLookup[] = [];

  /**
   * Lookup array for field types in the Dataverse
   */
  typeLookup: DataverseLookup[] = [];

  /**
   * Lookup array for environments in the Dataverse
   */
  envLookup: DataverseLookup[] = [];
  /**
  * Private variable representing the ID of the export template in the Dataverse
  */
  private exportTemplateDtoId: number;

  /**
   * Private variable representing the export template model in the Dataverse
   */
  private exportTemplateModel: ExportTemplateModel;

  /**
   * Private variable representing the step of the export template in the Dataverse
   */
  private exportTemplateStep = 1;

  /**
   * Private variable indicating whether a new export template is created in the Dataverse
   */
  private isCreatedNewExportTemplate = false;

  /**
   * Private variable indicating whether a copy of the export template is created in the Dataverse
   */
  private isCreatedCopyExporTemplate = false;

  /**
   * Private variable representing the search group in the Dataverse
   */
  private searchGroup: DtoGroup = new DtoGroup();

  /**
   * Private variable representing the search DTO (Data Transfer Object) in the Dataverse
   */
  private searchDto: Dto = new Dto();

  /**
   * Private BehaviorSubject representing the active view in the Dataverse
   */
  private _activeView$: BehaviorSubject<DataverseViewEnum> = new BehaviorSubject(DataverseViewEnum.None);

  /**
   * Private BehaviorSubject representing the active action in the Dataverse
   */
  private _activeAction$: BehaviorSubject<DataverseActionsEnum> = new BehaviorSubject(DataverseActionsEnum.DEVOverView);

  /**
   * Private BehaviorSubject representing the active template in the Dataverse
   */
  private _activeTemplate$: BehaviorSubject<any> = new BehaviorSubject(null);

  /**
   * Private BehaviorSubject representing the active sub-action in the Dataverse
   */
  private _activeSubAction$: BehaviorSubject<DataverseSubActionEnum> = new BehaviorSubject(DataverseSubActionEnum.None);

  /**
  * Private BehaviorSubject representing if the export model should be updated
  */
  private _updateExportTemplateModel$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  /**
   * Private BehaviorSubject representing the active DTO (Data Transfer Object) in the Dataverse
   */
  private _activeDto$: BehaviorSubject<Dto> = new BehaviorSubject<Dto>(null);

  /**
 * Private BehaviorSubject representing the DTO List in the Dataverse
 */
  private _dtoList$: BehaviorSubject<Dto[]> = new BehaviorSubject<Dto[]>([]);

  /**
   * Private BehaviorSubject representing the selected fields in the Dataverse
   */
  private _selectedFields$: BehaviorSubject<Field[]> = new BehaviorSubject<Field[]>([]);

  /**
   * Public array representing the selected fields in the Dataverse
   */
  public selectedFields: Field[] = [];

  /**
   * Private BehaviorSubject representing the active field in the Dataverse
   */
  private _activeField$: BehaviorSubject<Field> = new BehaviorSubject<Field>(null);

  /**
   * Private variable representing the active DTO (Data Transfer Object) in the Dataverse
   */
  private _activeDto: Dto;

  /**
   * Public array representing the business unit groups in the Dataverse
   */
  public buGroups: DtoGroup[] = [];

  /**
   * Public array representing the development groups in the Dataverse
   */
  public devGroups: DtoGroup[] = [];

  /**
   * Public array representing the key user groups in the Dataverse
   */
  public keyUserGroups: DtoGroup[] = [];

  /**
   * Private BehaviorSubject representing the business unit groups in the Dataverse
   */
  private buGroups$: BehaviorSubject<DtoGroup[]> = new BehaviorSubject<DtoGroup[]>(null);

  /**
   * Private BehaviorSubject representing the development groups in the Dataverse
   */
  private devGroups$: BehaviorSubject<DtoGroup[]> = new BehaviorSubject<DtoGroup[]>(null);

  /**
   * Private BehaviorSubject representing the key user groups in the Dataverse
   */
  private keyUserGroups$: BehaviorSubject<DtoGroup[]> = new BehaviorSubject<DtoGroup[]>(null);

  /**
   * Private BehaviorSubject representing the search activity state in the Dataverse
   */
  private _isSearchActive$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Private variable representing the last search term in the Dataverse
   */
  private lastTerm: string;

  /**
   * Private variable representing the name of the active DTO (Data Transfer Object) in the Dataverse
   */
  private activeDtoName: string;

  /**
   * Public variable representing the ID of the active DTO (Data Transfer Object) in the Dataverse
   */
  public activeDtoid: number;

  /**
   * Public variable representing the active DTO (Data Transfer Object) in the Dataverse
   */
  public activeDto: Dto;

  /**
   * Public array representing the list of DTOs (Data Transfer Objects) in the Dataverse
   */
  public dtoList: Dto[] = [];

  /**
   * Private BehaviorSubject indicating whether to refresh the selected fields in the Dataverse
   */
  public refreshSelectedFields: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Observable representing the refresh state of the selected fields in the Dataverse
   */
  public refreshSelectedFields$: Observable<boolean>;

  /**
   * Private BehaviorSubject indicating whether to refresh the DTO groups in the Dataverse
   */
  public refreshDtoGroups: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Observable representing the refresh state of the DTO groups in the Dataverse
   */
  public refreshDtoGroups$: Observable<boolean>;

  /**
   * Private BehaviorSubject indicating whether the creation of a template is in progress in the Dataverse
   */
  public isCreateTemplateInProgres: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Constructor
   * @param apiService
   * @param eventBus
   */
  constructor(private apiService: DataverseApiService, private eventBus: EventBusService) {
    this.activeView$ = this._activeView$.asObservable();
    this.activeAction$ = this._activeAction$.asObservable();
    this.activeDto$ = this._activeDto$.asObservable();
    this.dtoList$ = this._dtoList$.asObservable();
    this.selectedFields$ = this._selectedFields$.asObservable();
    this.activeField$ = this._activeField$.asObservable();
    this.activeSubAction$ = this._activeSubAction$.asObservable();
    this.updateExportTemplateModel$ = this._updateExportTemplateModel$.asObservable();
    this.isSearchActive$ = this._isSearchActive$.asObservable();
    this.refreshSelectedFields$ = this.refreshSelectedFields.asObservable();
    this.refreshDtoGroups$ = this.refreshDtoGroups.asObservable();
    this.searchGroup.name = "Search";
    this.searchGroup.dtoList = [];
    this.searchDto.name = "Results";
    this.searchDto.fields = [];
    this.searchDto.isSearchModel = true;
    this.createNewExportTemplateModel();
    this.isCreatedNewExportTemplate = true;
  }

  /**
   * Field types
   */
  public fieldTypes: any[] = [
    'Undocumented',
    'Text',
    'Numeric',
    'Date',
    'Bool',
    'SingleSelect',
    'MultiSelect',
    'Retriever',
    'System',
    'DTO',
    'DTO list',
    'List'
  ];

  /**
   * Versions
   */
  public versions: any[] = [
    'Dev',
    'Release'
  ];

  /**
   * Search mode
   * @param isActive
   */
  public setSearchMode(isActive: boolean): void {
    this._isSearchActive$.next(isActive);
  }

  /**
   * Sets consider unique name in search
   * @param considerUniqueName
   */
  public setConsiderUniqueNameInSearch(considerUniqueName: boolean): void {
    this.considerUniqueNameInSearch = considerUniqueName;
    this.searchFields(this.lastTerm);
  }

  /**
   * Is it in search?
   * @returns
   */
  public isUniqueNameConsideredInSearch(): boolean {
    return this.considerUniqueNameInSearch;
  }

  /**
   * Clears the search
   */
  clearSearch() {
    this._isSearchActive$.next(false);
    this.searchGroup.dtoList = [];
    this.devGroups$.next(this.devGroups);
    this.buGroups$.next(this.buGroups);
    this.keyUserGroups$.next(this.keyUserGroups);
    if (this.activeDtoName === this.searchDto.name) {
      this.setActiveDto(null)
    }
  }

  /**
    * Refreshes the data
   */
  refreshData() {
    const tempActiveDto = { ...this.activeDto };
    this.setActiveDto(null);
    setTimeout(() => {
      this.devGroups$.next(this.devGroups);
      this.buGroups$.next(this.buGroups);
      this.keyUserGroups$.next(this.keyUserGroups);
      this.setActiveDto(tempActiveDto);
    }, 100)
  }

  /**
   * Searches through all fields, returns the ones containing term
   * @param term
   */
  searchFields(term: string) {
    this.apiService.SearchAllFields(term, this.considerUniqueNameInSearch).subscribe(fields => {
      this.lastTerm = term;
      this.searchDto.fields = [];
      this.searchGroup.dtoList = [];
      this.dtoList = this.dtoList.filter((dto) => !dto.isSearchModel);
      this.searchDto.fields = fields;
      this.searchGroup.dtoList.push(this.searchDto);
      this.devGroups$.next(this.devGroups);
      this.buGroups$.next(this.buGroups);
      this.keyUserGroups$.next(this.keyUserGroups);
      this.setActiveDto(this.searchDto);
    })
  }

  /**
   * Sets the development groups, business unit groups, and optional key user groups.
   */
  public setGroups(dev: DtoGroup[], bu: DtoGroup[], keyUserGroup: DtoGroup[] = null) {
    dev = [this.searchGroup].concat(dev);
    bu = [this.searchGroup].concat(bu);
    if (keyUserGroup != null) {
      keyUserGroup = [this.searchGroup].concat(keyUserGroup);
      this.keyUserGroups$.next(keyUserGroup);
      this.keyUserGroups = keyUserGroup;
    }
    this.buGroups$.next(bu);
    this.devGroups$.next(dev);
    this.devGroups = dev;
    this.buGroups = bu;
  }

  /**
  * Retrieves the groups based on the specified flags for development and key user.
  */
  public getGroups(isDev: boolean, isKeyUser = false): Observable<DtoGroup[]> {
    this.displayDataLoader(false);
    if (isDev) {
      this.setDtoList(this.devGroups);
      return this.devGroups$;
    }
    else if (isKeyUser) {
      this.setDtoList(this.keyUserGroups);
      return this.keyUserGroups$;
    }
    else {
      this.setDtoList(this.buGroups);
      return this.buGroups$;
    }
  }

  /**
   * Sets the reference tables for development status, environment, and type.
   */
  public setRefTables(devStatus: DataverseLookup[], env: DataverseLookup[], type: DataverseLookup[]) {
    this.devStatusLookup = devStatus;
    this.envLookup = env;
    this.typeLookup = type;
  }

  /**
   * Retrieves the environment name based on the specified ID.
   */
  public getEnvName(id: number): string {
    return id ? this.envLookup.find(e => e.id === id).description : '';
  }

  /**
   * Retrieves the development status based on the specified ID.
   */
  public getDevStatus(id: number): string {
    return id ? this.devStatusLookup.find(e => e.id === id).description : '';
  }

  /**
   * Retrieves the type based on the specified ID.
   */
  public getType(id: number): string {
    return id ? this.typeLookup.find(e => e.id === id).description : '';
  }

  /**
   * Checks if a DTO with the specified ID is selected.
   */
  public isDtoSelected(id: number) {
    return this._activeDto && this._activeDto.id === id;
  }

  /**
   * Sets the active Dataverse view.
   */
  public setActiveDataverseView(newView: DataverseViewEnum) {
    this._activeView$.next(newView);
  }

  /**
   * Sets the active Dataverse action.
   */
  public setActiveDataverseAction(newAction: DataverseActionsEnum) {
    this._activeAction$.next(newAction);
  }

  /**
   * Sets the active Dataverse sub-action.
   */
  public setActiveDataverseSubAction(newAction: DataverseSubActionEnum) {
    this._activeSubAction$.next(newAction);
  }

  /**
   * Sets the update export template flag.
   */
  public updateExportTemplateModel(updateModel: boolean) {
    this._updateExportTemplateModel$.next(updateModel);
  }

  /**
   * Updates the selected fields with the provided field.
   */
  public updateSelectedFields(field: Field): boolean {
    const selectedField = this.selectedFields.find(f => f.oid === field.oid);
    if (selectedField) {
      const idx = this.selectedFields.indexOf(selectedField);
      this.selectedFields.splice(idx, 1);
    } else {
      this.selectedFields.push(field);
    }
    this.setActiveField(field);
    this._selectedFields$.next(this.selectedFields);

    if (this.selectedFields.length == 0) {
      this.setActiveField(null);
    }

    return !selectedField;
  }

  /**
   * Updates the selected template fields with the provided DTO.
   */
  public updateSelectedTemplateFields(dto: Dto): boolean {
    const selectedFields = dto.fields;
    if (this.selectedFields.filter(field => field.dtoId == selectedFields[0].dtoId).length !== selectedFields.length) {
      this.selectedFields.push(...selectedFields);
    } else {
      const firstTemplateField = this.selectedFields.find(field => field.dtoId == selectedFields[0].dtoId);
      this.selectedFields.splice(this.selectedFields.indexOf(firstTemplateField), selectedFields.length);
    }
    this._selectedFields$.next(this.selectedFields);

    return this.selectedFields.length > 0;
  }

  /**
  * Sets the active DTO and updates related properties based on the provided DTO.
  */
  public setActiveDto(dto: Dto) {
    this._activeField$.next(null);
    this._activeDto = dto;
    this._activeDto$.next(dto);
    this.activeDtoName = dto?.name;
    this.activeDtoid = dto?.id;
    this.activeDto = dto;
    if (dto?.id) {
      this.apiService.getFieldsForDTO(dto.id).subscribe(fieldsforDTO => {
        if (fieldsforDTO) {
          dto.fields = fieldsforDTO;
          this._activeDto = dto;
          this._activeDto$.next(dto);
          this.activeDtoName = dto?.name;
          this.activeDtoid = dto?.id;
          this.activeDto = dto;
        }
      });
    }
  }

  /**
   * Removes a field from the active DTO.
   */
  public removeFieldFromActiveDto(field: Field) {
    const fieldIndex = this._activeDto.fields.indexOf(field);
    this._activeDto.fields.splice(fieldIndex, 1);
    this.removeFieldFromBucket(field.oid);
    this._activeDto$.next(this._activeDto);
  }

  /**
   * Sets the active DTO based on the provided ID.
   */
  public setActiveDtoFromBucket(id: number) {
    this.setActiveDto(this.dtoList.find(g => g.id === id));
  }

  /**
   * Sets the active field.
   */
  public setActiveField(field: Field) {
    this._activeField$.next(field);
  }

  /**
   * Sets the active template.
   */
  public setActiveTemplate(template: any) {
    this._activeTemplate$.next(template);
  }

  /**
 * Sets the DTO List for field form.
 */
  public setDtoListForFieldForm(dtoList: Dto[]) {
    this._dtoList$.next(dtoList);
  }

  /**
   * Returns an Observable for the active template.
   */
  public getActiveTemplate$(): Observable<any> {
    return this._activeTemplate$;
  }

  /**
   * Returns an Observable for the active DTO.
   */
  public getActiveDto$(): Observable<Dto> {
    return this.activeDto$;
  }

  /**
  * Returns an Observable for the DTO List for field form.
  */
  public getDtoListForFieldForm$(): Observable<Dto[]> {
    return this.dtoList$;
  }

  /**
   * Returns an Observable for the selected fields.
   */
  public getSelectedFields$(): Observable<Field[]> {
    return this.selectedFields$;
  }

  /**
   * Returns an Observable for the active field.
   */
  public getActiveField$(): Observable<Field> {
    return this.activeField$;
  }

  /**
   * Removes a field from the bucket.
   */
  public removeFieldFromBucket(id: number) {
    const selectedField = this.selectedFields.find((f) => f.oid === id);
    selectedField.selected = this.updateSelectedFields(selectedField);
    this.refreshSelectedFields.next(true);
    this.setActiveDataverseSubAction(DataverseSubActionEnum.FieldsBucket);
  }

  /**
   * Sets the DTO list based on the provided groups.
   */
  public setDtoList(groups: DtoGroup[]) {
    this.dtoList = [];
    groups.forEach((r) => {
      r.dtoList.forEach((g) => this.dtoList.push(g));
    })
    this.setDtoListForFieldForm(this.dtoList);
  }

  /**
   * Retrieves similar fields based on the provided criteria.
   */
  public getSimilarFields(boName: string, uniqueName: string, filterByBoNameOnly: boolean): string[] {
    const similarFields: string[] = [];
    this.dtoList.forEach((dto) => {
      dto.fields.forEach((f) => {
        if (f.boName === boName && (f.uniqueName?.toLowerCase().indexOf(uniqueName?.toLowerCase()) >= 0)) {
          if (f.uniqueName)
            similarFields.push(f.uniqueName)
        }
      });
    })

    return similarFields;
  }

  /**
   *  Gets the dto name
   */
  public getDtoName(id: number) {
    const dto = this.dtoList.find(g => g.id === id);
    if (dto) {
      if (dto.consultingName) {
        return dto.consultingName;
      }
      if (dto.name) {
        return dto.name;
      }
      return 'DTO HAS NO NAME!';
    }
    return 'DTO NOT FOUND!';
  }

  /**
   * Gets dto
   */
  public getDto(id: number) {
    return this.dtoList.find(g => g.id === id);
  }

  /**
   * Clears the bucket
   */
  public clearBucket() {
    for (const selectedField of this.selectedFields) {
      selectedField.selected = false;
    }
    this.selectedFields = [];
    this._selectedFields$.next([]);
    this.refreshData();
  }

  /**
   * Clears the bucket without a refresh
   */
  public clearBucketWithoutRefresh() {
    for (const selectedField of this.selectedFields) {
      selectedField.selected = false;
    }
    this.selectedFields = [];
    this._selectedFields$.next([]);
  }

  /**
   * Exports the field of a BO to Excel
   * @param boName BO name
   */
  filterSelectedFieldsForExport(boName?: string) {
    // for now we won't use filter for boName because Dataverse have to be cleaned-up..
    // f.e. fields of "VALOREM Assumption" use both "VALOREM Assumptions" and "VALOREM Assumption" for BoName
    // or fields of "VALOREM Property" use just "VALOREM Properties" for BoName
    // in theory we get same list of fields as we filter cleaned-up fields
    const skipBoNameCheck = true;
    return boName
      ? this.activeDto.fields.filter(x => skipBoNameCheck || x.boName == boName).map(x => { return x.oid })
      : this.selectedFields.map(x => { return x.oid });
  }

  /**
   * Refreshes key user view
   */
  public refreshKeyUserView() {
    this.setActiveDto(null);
    this.setActiveField(null);
    this.setActiveTemplate(null);
    this.clearBucket();

    //Task 17117: Key user view - reload after template creation
    this.apiService.keyUserOverview().subscribe(data => {
      if (data) {
        this.setGroups(this.devGroups, this.buGroups, data.keyUserGroups);
        this.refreshDtoGroups.next(true);
      }
    });
  }

  /**
 * Show or hide data loader
 * @param state State
 */
  displayDataLoader(state: boolean) {
    this.eventBus.broadcastEvent("dataLoader", {
      displayLoader: ()=> {
        return state;
      }
    });
  }

  /**
   * Retrive export template model
   */
  getExportTemplateModel() {
    return this.exportTemplateModel;
  }

  /**
   * Create new export template model
   */
  createNewExportTemplateModel() {
    this.isCreatedNewExportTemplate = true;
    this.isCreatedCopyExporTemplate = false;
    this.changeExportTemplateStep(1);
    this.exportTemplateModel = new ExportTemplateModel();
  }

  /**
   * Create copy export template model
   * @param data Data that should be copied
   */
  createCopyExportTemplateModel(data: any) {
    this.isCreatedCopyExporTemplate = true;
    this.isCreatedNewExportTemplate = false;
    this.changeExportTemplateStep(2);
    this.setExportTemplateDtoId(this.activeDto.id);
    this.exportTemplateModel = new ExportTemplateModel(data, this.isCreatedCopyExporTemplate);
  }

  /**
   * Set export template model
   * @param exportTemplateGroup
   */
  setExportTemplateModel(exportTemplateFormGroup: UntypedFormGroup) {
    this.exportTemplateModel = new ExportTemplateModel(exportTemplateFormGroup?.value ?? null);
  }

  /**
   * Retrive export template step
   */
  getExportTemplateStep(): number {
    return this.exportTemplateStep;
  }

  /**
   * Set export template step
   * @param stepNumber Step number
   */
  changeExportTemplateStep(stepNumber: number) {
    this.exportTemplateStep = stepNumber;
  }

  /**
   * Retrive infomation about created new export template
   */
  getIsCreatedNewExportTemplate() {
    return this.isCreatedNewExportTemplate;
  }

  /**
   * Retrive information about created copy of existing export template
   * @returns
   */
  getIsCreatedCopyExportTemplate() {
    return this.isCreatedCopyExporTemplate;
  }

  /**
   * Retrive export template dto id
   * @returns
   */
  getExportTemplateDtoId() {
    return this.exportTemplateDtoId;
  }

  /**
   * Set export template dto id
   * @param exportTemplateDtoId Export template dto id
   */
  setExportTemplateDtoId(exportTemplateDtoId: number) {
    this.exportTemplateDtoId = exportTemplateDtoId;
  }

  /**
   * Sets Create Template In Progress
   * @param value
   */
  setIsCreateTemplateInProgress(value: boolean) {
    this.isCreateTemplateInProgres.next(value);
  }
}
